import React, { useEffect, useState } from "react";
import styles from '../../programDirector/createInstructor/CreateInstructor.module.scss';
import Breadcrumbs from "../../../components/breadcrumbs/Breadcrumbs";
import ClinicalCoordinatorForm from "../../../components/clinicalCoordinatorForm/ClinicalCoordinatorForm";
import axiosClient from "../../../api/axiosClient";
import { useNavigate, useParams } from "react-router-dom";
import LoadingOverlay from "../../../components/loadingOverlay/LoadingOverlay";
import { useToasts } from "../../../contexts/ToastContext";
import { formatPhoneNumber } from "../../../utils/stringUtils";

const CreateClinicalCoordinator = () => {
  const [isLoading, setIsLoading] = useState(false);
  const initialFormData = {
    profilePicture: null,
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    healthSystemIds: [],
    clinicalSiteIds: [],
  };
  const [formData, setFormData] = useState(initialFormData);
  const [healthSystemList, setHealthSystemsList] = useState([]);
  const [clinicalSiteList, setClinicalSiteList] = useState([]);
  const { coordinatorId } = useParams();
  const { showToast } = useToasts();
  const navigate = useNavigate();

  useEffect(() => {
    axiosClient.get('health-systems').then((response) => {
      setHealthSystemsList(response.data.map((item) => ({ value: item.id, label: item.name })));
    });
  }, []);

  useEffect(() => {
    if (formData.healthSystemIds.length === 0) {
      setClinicalSiteList([]);
      setFormData((prev) => ({ ...prev, clinicalSiteIds: [] }));
      return;
    }
    axiosClient.get('clinical-sites', {
      params: { healthSystemIds: formData.healthSystemIds.join(',') }
    }).then((response) => {
      const availableIds = response.data.map(item => item.id);
      setClinicalSiteList(response.data.map((item) => ({ value: item.id, label: item.name })));
      setFormData((prev) => ({
        ...prev,
        clinicalSiteIds: prev.clinicalSiteIds.filter(id => availableIds.includes(id))
      }));
    });
  }, [formData.healthSystemIds]);

  useEffect(() => {
    if (!coordinatorId) return;
    setIsLoading(true);
    axiosClient.get(`users/clinical-coordinator/${coordinatorId}`)
      .then((response) => {
        setFormData((prev) => ({
          ...prev,
          profilePicture: response.data?.profilePicture,
          firstName: response.data?.firstName,
          lastName: response.data?.lastName,
          email: response.data?.email,
          phone: response.data?.phone !== '0' ? formatPhoneNumber(response.data?.phone) : '',
          healthSystemIds: response.data?.healthSystems?.map(item => item.id) ?? [],
          clinicalSiteIds: response.data?.clinicalSites?.map(item => item.id) ?? [],
        }));
      })
      .catch((error) => showToast(error.message, 'error'))
      .finally(() => setIsLoading(false));
  }, [coordinatorId, showToast]);

  const handleSave = async (imageFile) => {
    const data = {
      firstName: formData.firstName,
      lastName: formData.lastName,
      email: formData.email,
      mobileNumber: formData.phone.replace(/\D/g, ''),
      healthSystemIds: formData.healthSystemIds,
      clinicalSiteIds: formData.clinicalSiteIds,
    };
    const updatedFormData = new FormData();
    for (let key in data) {
      if (Object.prototype.hasOwnProperty.call(data, key)) {
        if (Array.isArray(data[key])) {
          data[key].forEach(item => {
            if (item != null) updatedFormData.append(`${key}[]`, item);
          });
        } else {
          if (data[key] != null) updatedFormData.append(key, data[key]);
        }
      }
    }
    if (imageFile) updatedFormData.append('profilePicture', imageFile);

    setIsLoading(true);
    const url = coordinatorId
      ? `users/clinical-coordinator/${coordinatorId}`
      : 'clinical-coordinator/create';
    const method = coordinatorId ? 'patch' : 'post';

    await axiosClient[method](url, updatedFormData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    })
      .then((response) => {
        showToast(response.message);
        navigate('/clinical-coordinators');
      })
      .catch((error) => showToast(error.message, 'error'))
      .finally(() => {
        setIsLoading(false);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      });
  };

  return (
    <>
      {isLoading && <LoadingOverlay />}
      <div className={styles.breadcrumbs}>
        <Breadcrumbs
          breadcrumbs={[
            { label: 'Clinical Coordinator Management', path: '/clinical-coordinators' },
            { label: coordinatorId ? 'Edit Clinical Coordinator' : 'Create Clinical Coordinator' }
          ]}
        />
      </div>
      <div className={styles.container}>
        <ClinicalCoordinatorForm
          formData={formData}
          healthSystemList={healthSystemList}
          clinicalSiteList={clinicalSiteList}
          isEdit={!!coordinatorId}
          onDataChange={(updatedData) => setFormData((prev) => ({ ...prev, ...updatedData }))}
          onSubmit={handleSave}
        />
      </div>
    </>
  );
};

export default CreateClinicalCoordinator;
